/**
 * Conexoes PowerBI - o service principal (Entra) com que falamos com o Power BI.
 *
 * O SEGREDO nao esta aqui e nao entra nesta tela: o que se guarda e `secret_ref`, o nome da chave
 * dentro do `POWERBI_CONNECTIONS_JSON` do backend. Por isso a `connection_key` nao e rotulo
 * livre - trocar ela sem trocar a mesma chave no ambiente quebra a conexao em silencio.
 */

import { useQuery } from '@tanstack/react-query'

import { fetchConnections } from '../api/catalog'
import { RESOURCE_CONEXOES } from '../api/types'
import type { PowerBiConnection } from '../api/types'
import { usePermissoes } from '../auth/permissoes'
import type { CampoMeta } from '../listagem/campos'
import { IdCurto, Listagem, Marca } from '../listagem/Listagem'
import { Resultado } from '../listagem/Resultado'

const CAMPOS: Array<CampoMeta<PowerBiConnection>> = [
  {
    nome: 'name',
    rotulo: 'Nome',
    grupo: 'Identificação',
    edicao: { tipo: 'texto', dica: 'Rótulo livre, para você identificar.' },
  },
  {
    nome: 'connection_key',
    rotulo: 'Chave da conexão',
    grupo: 'Identificação',
    formato: 'codigo',
    nota: 'É ela que liga esta conexão ao segredo guardado no ambiente do backend.',
    edicao: {
      tipo: 'texto',
      dica: 'Minúsculas, números, _ e -. Trocar aqui exige trocar a mesma chave no POWERBI_CONNECTIONS_JSON.',
      exemplo: 'synsaude',
    },
  },
  { nome: 'client_slug', rotulo: 'Cliente', grupo: 'Identificação', formato: 'codigo' },
  {
    nome: 'microsoft_tenant_id',
    rotulo: 'Entra Tenant ID',
    grupo: 'Microsoft Entra',
    formato: 'identificador',
    edicao: { tipo: 'texto', dica: 'Directory (tenant) ID: Azure Portal → Microsoft Entra ID → Overview.' },
  },
  {
    nome: 'entra_client_id',
    rotulo: 'Entra Client ID',
    grupo: 'Microsoft Entra',
    formato: 'identificador',
    edicao: { tipo: 'texto', dica: 'Application (client) ID: Azure Portal → App registrations → seu app.' },
  },
  {
    nome: 'secret_ref',
    rotulo: 'Referência do segredo',
    grupo: 'Microsoft Entra',
    formato: 'codigo',
    nota: 'Um NOME que aponta para o segredo. O valor real nunca passa por esta tela nem pelo banco.',
    edicao: { tipo: 'texto', dica: 'Nunca o segredo em si.' },
  },
  {
    nome: 'is_active',
    rotulo: 'Conexão ativa',
    grupo: 'Estado',
    formato: 'booleano',
    nota: 'Inativa, nenhum dataset ou relatório que dependa dela consegue autenticar.',
    edicao: { tipo: 'booleano' },
  },
  { nome: 'client_id', rotulo: 'Cliente (identificador)', grupo: 'Registro', formato: 'identificador' },
  { nome: 'id', rotulo: 'Identificador no catálogo', grupo: 'Registro', formato: 'identificador' },
]

export function ConexoesPage() {
  const { pode } = usePermissoes()
  const consulta = useQuery({ queryKey: ['powerbi-connections'], queryFn: fetchConnections })

  return (
    <>
      <Resultado consulta={consulta}>
        {(conexoes) => (
          <Listagem<PowerBiConnection>
            itens={conexoes}
            chave="powerbi-connections"
            entidade="powerbi-connections"
            rotulo="conexão"
            rotuloPlural="conexões"
            campos={CAMPOS}
            podeGerenciar={pode(RESOURCE_CONEXOES, 'manage')}
            idDe={(item) => item.id}
            nomeDe={(item) => item.name}
            colunas={[
              { titulo: 'Nome', valor: (item) => item.name },
              { titulo: 'Chave', valor: (item) => <code>{item.connection_key}</code> },
              { titulo: 'Cliente', valor: (item) => item.client_slug },
              { titulo: 'Entra Client ID', valor: (item) => <IdCurto valor={item.entra_client_id} /> },
              { titulo: 'Ativa', valor: (item) => <Marca valor={item.is_active} /> },
            ]}
            avisoDeEdicao="Trocar a chave da conexão exige atualizar a mesma chave no POWERBI_CONNECTIONS_JSON do backend. Sem isso, a conexão para de autenticar."
            vazio="Nenhuma conexão cadastrada. O cadastro é feito no onboarding, passo 3."
          />
        )}
      </Resultado>
    </>
  )
}
